import type { ReceiptInput } from '@chukua/common';
import { Alert, Button } from 'antd';
import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { PageHeader } from '../../../../../shared/components/PageHeader';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { withinSection } from '../../../../../shared/components/section-trail';
import { buildReceiptColumns } from '../components/build-receipt-columns';
import { ReceiptActionModal } from '../components/ReceiptActionModal';
import { RecordReceiptDrawer } from '../components/RecordReceiptDrawer';
import { ReceiptsTable } from '../components/ReceiptsTable';
import { useReceiptAction } from '../hooks/use-receipt-action';
import { useReceiptList } from '../hooks/use-receipt-list';
import { useReceiptPermissions } from '../hooks/use-receipt-permissions';
import { useRecordReceipt } from '../hooks/use-record-receipt';

/** Money customers have paid in: browse and search them, and, with `payment.record`, record a new one or mark one cleared, bounced or reversed. */
export function ReceiptsPage() {
  const { state, list, isRefreshing, hasReloadFailed, reload } = useReceiptList();
  const { canRecord } = useReceiptPermissions();
  const { error, saving, record, resetError } = useRecordReceipt();
  const change = useReceiptAction(reload);
  const [drawerOpen, setDrawerOpen] = useState(false);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  async function recordAndReload(input: ReceiptInput): Promise<boolean> {
    const recorded = await record(input);
    if (recorded) await reload();
    return recorded;
  }

  function openDrawer() {
    resetError();
    setDrawerOpen(true);
  }

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('billing')}
        title="Receipts"
        description="Money customers have paid in, by M-Pesa, cheque or bank transfer."
        actions={
          canRecord && (
            <Button type="primary" icon={<Plus className="size-4" />} onClick={openDrawer}>
              Record receipt
            </Button>
          )
        }
      />
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load receipts" description="Something went wrong loading the receipts. Try reloading the page." />
      )}
      {hasReloadFailed && (
        <Alert type="warning" showIcon className="mb-4" message="The receipt was saved, but the list could not be refreshed. Reload the page to see it." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading receipts" withSearch />}
      {state.status === 'loaded' && (
        <ReceiptsTable
          page={state.page}
          columns={buildReceiptColumns({ list, onAction: canRecord ? change.open : undefined })}
          list={list}
          isRefreshing={isRefreshing}
          canRecord={canRecord}
        />
      )}
      {canRecord && <RecordReceiptDrawer open={drawerOpen} error={error} submitting={saving} onRecord={recordAndReload} onClose={() => setDrawerOpen(false)} />}
      {canRecord && (
        <ReceiptActionModal target={change.target} error={change.error} submitting={change.saving} onSubmit={change.submit} onClose={change.close} />
      )}
    </>
  );
}
